import React, { useEffect, useMemo, useState } from "react";

import { TextField, Box, Typography, Stack } from "@mui/material";

import {
    useForm,
    useWatch,
    useFieldArray,
    FormProvider,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import StepContainer from "../../components/StepContainer";
import { CommonLandingPageSteps } from "../../AddEditLandingPage.types";
import {
    renderTemplateInput,
    validateHtmlSnippet,
} from "../LandingPageTemplateSelectStep/LandingPageTemplateSelectStep.helpers";
import useUpdateEffect from "hooks/useUpdateEffect";

const generateDynamicSchema = (fields) => {
    const shape = {};
    fields.forEach((field) => {
        let validator;

        switch (field.type) {
            case "input":
            case "multiline_input":
                validator = yup.string();
                break;

            case "image":
                validator = yup.mixed();
                break;

            case "combobox":
                validator = yup
                    .string()
                    .oneOf(field.options, `Invalid value for ${field.name}`);
                break;

            case "multi_select":
                validator = yup
                    .array()
                    .of(yup.string().oneOf(field.options || []));
                break;

            default:
                validator = yup.mixed();
                break;
        }

        // Special case for logo
        if (field.name === "logo") {
            return;
        } else if (field.name === "video") {
            return;
        } else {
            // Regular required handling
            if (field.required) {
                validator = validator.required(
                    `${field.description || field.name} is required`,
                );
            } else {
                validator = validator.notRequired().nullable();
            }
        }

        shape[field.name] = validator;
    });

    return yup.object().shape(shape);
};

export default function PageContentStep({
    onSave,
    form,
    selectedTemplatedID,
    isLandingPageFormLoading,
    isStepCompleted,
    templates,
    mmpDetails,
    appDetails,
    customSources,
    dispatchGetAppDetails,
    gamificationTemplates,
    setIsStepDirty,

    mmpField,
    appleIdField,
    selectedAppTypes,
    gamificationType,
    selectedCustomSourceId,
}) {
    const [selectedGamificationTemplate, setSelectedGamificationTemplate] =
        useState(null);
    const [htmlValidationMessage, setHTMLValidationMessage] = useState({
        isValid: true,
    });

    // Add state for funnel validation errors
    const [funnelValidationErrors, setFunnelValidationErrors] = useState({});
    const [isSubmitPressed, setIsSubmitPressed] = useState(false);

    const selectedTemplate = useMemo(
        () =>
            templates?.data?.find(
                (template) => template?.id === selectedTemplatedID,
            ),
        [templates, selectedTemplatedID],
    );
    const names = [
        "mmp",
        "app_type",
        "cpp_android",
        "cpp_ios",
        "code",
        "target_link",
        "theme",
        "steps",
    ];
    const fields = useMemo(
        () =>
            templates?.data
                ?.find((template) => template?.id === selectedTemplatedID)
                ?.fields?.filter((field) => !names.includes(field?.name)),
        [selectedTemplatedID],
    );
    const populatedCustomSource = useMemo(
        () =>
            customSources?.data?.find(
                (source) => source?.id === selectedCustomSourceId,
            ),
        [selectedCustomSourceId, customSources?.data],
    );
    const schema = useMemo(() => {
        return generateDynamicSchema(fields || []);
    }, [fields]);
    const hookForm = useForm({
        defaultValues: {
            ...form,
        },
        resolver: yupResolver(schema),
    });
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
        register,
        watch,
        setValue,
    } = hookForm;

    const gamificationItemsArrayForm = useFieldArray({
        control,
        name: "gamification_items",
    });
    const onSubmit = (data) => {
        const hasLogoField = fields?.find((item) => item?.name === "logo");
        if (hasLogoField && !data.logo && !data.logo_url) {
            hookForm.setError("logo", {
                type: "manual",
                message: "Logo is required",
            });
            return;
        }
        onSave({
            stepName: CommonLandingPageSteps.PAGE_CONTENT,
            data,
        });
    };

    // Function to clear errors when input changes
    const clearFunnelErrors = (stepIndex, fieldName) => {
        setFunnelValidationErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[stepIndex]) {
                delete newErrors[stepIndex][fieldName];
                if (Object.keys(newErrors[stepIndex]).length === 0) {
                    delete newErrors[stepIndex];
                }
            }
            return newErrors;
        });
    };

    // Function to get error for a specific field
    const getFunnelFieldError = (stepIndex, fieldName) => {
        return funnelValidationErrors[stepIndex]?.[fieldName];
    };

    // Function to check if field should show error styling
    const shouldShowError = (stepIndex, fieldName) => {
        return isSubmitPressed && getFunnelFieldError(stepIndex, fieldName);
    };

    useEffect(() => {
        reset(form);
        // Clear funnel validation errors when form is reset
        setFunnelValidationErrors({});
        setIsSubmitPressed(false);
    }, [form]);
    useUpdateEffect(() => {
        setIsStepDirty(isDirty);
    }, [isDirty]);
    useEffect(() => {
        if (gamificationType && gamificationTemplates?.data) {
            const populatedGamificationTemplate =
                gamificationTemplates?.data?.find(
                    (gamificationTemplate) =>
                        gamificationTemplate?.type === gamificationType,
                );
            setSelectedGamificationTemplate(populatedGamificationTemplate);

            const items = populatedGamificationTemplate?.settings?.find(
                (item) => item?.name === "gamification_items",
            );

            gamificationItemsArrayForm?.fields?.length < items?.min_count &&
                reset({
                    ...watch(),
                    gamification_items: [
                        ...(gamificationItemsArrayForm?.fields?.length > 0
                            ? gamificationItemsArrayForm?.fields
                            : []),
                        ...Array.from({
                            length:
                                items?.min_count -
                                gamificationItemsArrayForm?.fields?.length,
                        }).fill({}),
                    ],
                });
        }
    }, [gamificationType, gamificationTemplates]);
    useEffect(() => {
        if (
            selectedCustomSourceId &&
            !!customSources?.data?.length &&
            selectedTemplate?.name === "lead"
        ) {
            reset({
                ...watch(),
                lead_form_fields: populatedCustomSource?.fields?.map(
                    (field) => ({
                        ...field,
                        label: field?.name,
                    }),
                ),
            });
        }
    }, [customSources, selectedCustomSourceId]);

    const htmlDescription = useWatch({ control, name: "html_description" });

    useEffect(() => {
        setHTMLValidationMessage(validateHtmlSnippet(htmlDescription || ""));
    }, [htmlDescription]);

    const logo = useWatch({ control, name: "logo" });
    const logo_url = useWatch({ control, name: "logo_url" });

    useEffect(() => {
        if ((logo || logo_url) && errors.logo) {
            hookForm.clearErrors("logo");
        }
    }, [logo, logo_url]);

    return (
        <StepContainer
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isLoading={isLandingPageFormLoading}
        >
            {/* Step Header */}
            <Box sx={{ mb: { xs: 3, md: 3 } }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        fontSize: {
                            xs: "20px",
                            sm: "24px",
                            md: "28px",
                            lg: "32px",
                        },
                        mb: { xs: 1, md: 2 },
                        textAlign: { xs: "center", md: "left" },
                        lineHeight: { xs: 1.2, md: 1.3 },
                    }}
                >
                    Page Content
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        fontSize: { xs: "13px", sm: "14px", md: "16px" },
                        textAlign: { xs: "center", md: "left" },
                        lineHeight: 1.5,
                        px: { xs: 1, md: 0 }, // Add some padding on mobile
                    }}
                >
                    Customize the content and appearance of your landing page
                </Typography>
            </Box>

            <FormProvider {...hookForm}>
                <Stack spacing={{ xs: 3, md: 4 }} sx={{ width: "100%" }}>
                    {/* Dynamic Fields */}
                    {fields?.map((field) =>
                        renderTemplateInput({
                            hookForm,
                            field,
                            errors,
                            htmlValidationMessage,
                        }),
                    )}

                    {/* Gamification Template Fields */}
                    {selectedTemplate?.name === "gamification" &&
                        selectedGamificationTemplate?.settings?.map((field) =>
                            renderTemplateInput({
                                hookForm,
                                field,
                                gamificationItemsArrayForm,
                                selectedAppTypes,
                                dispatchGetAppDetails,
                                appleIdField,
                                mmpField,
                                appDetails,
                            }),
                        )}

                    {/* Lead Form Fields */}
                    {!!customSources?.data?.length &&
                        !customSources?.isLoadingList &&
                        selectedTemplate?.name === "lead" &&
                        populatedCustomSource && (
                            <Box
                                sx={{
                                    background: "#F8F9FA",
                                    p: { xs: 2, md: 3 },
                                    borderRadius: 3,
                                    border: "1px solid #EDF0F6",
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        mb: { xs: 2, md: 3 },
                                        fontSize: { xs: "18px", md: "20px" },
                                    }}
                                >
                                    Form Items
                                </Typography>

                                <Stack spacing={{ xs: 2, md: 3 }}>
                                    {populatedCustomSource?.fields?.map(
                                        (item, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: {
                                                        xs: "1fr",
                                                        sm: "repeat(2, 1fr)",
                                                        md: "repeat(4, 1fr)",
                                                    },
                                                    gap: { xs: 2, md: 2 },
                                                    p: { xs: 2, md: 3 },
                                                    backgroundColor: "#FFFFFF",
                                                    borderRadius: 2,
                                                    border: "1px solid #E0E6F0",
                                                }}
                                            >
                                                <TextField
                                                    label="Label"
                                                    {...register(
                                                        `lead_form_fields[${index}].label`,
                                                    )}
                                                    fullWidth
                                                    required
                                                    sx={{
                                                        "& .MuiInputBase-root":
                                                            {
                                                                height: {
                                                                    xs: "48px",
                                                                    md: "56px",
                                                                },
                                                                fontSize: {
                                                                    xs: "16px",
                                                                    md: "18px",
                                                                },
                                                            },
                                                    }}
                                                />
                                                <TextField
                                                    label="ID"
                                                    {...register(
                                                        `lead_form_fields[${index}].id`,
                                                    )}
                                                    fullWidth
                                                    disabled
                                                    sx={{
                                                        "& .MuiInputBase-root":
                                                            {
                                                                height: {
                                                                    xs: "48px",
                                                                    md: "56px",
                                                                },
                                                                fontSize: {
                                                                    xs: "16px",
                                                                    md: "18px",
                                                                },
                                                                backgroundColor:
                                                                    "#F5F5F5",
                                                            },
                                                    }}
                                                />
                                                <TextField
                                                    label="Name"
                                                    value={item?.name}
                                                    fullWidth
                                                    disabled
                                                    sx={{
                                                        "& .MuiInputBase-root":
                                                            {
                                                                height: {
                                                                    xs: "48px",
                                                                    md: "56px",
                                                                },
                                                                fontSize: {
                                                                    xs: "16px",
                                                                    md: "18px",
                                                                },
                                                                backgroundColor:
                                                                    "#F5F5F5",
                                                            },
                                                    }}
                                                />
                                                <TextField
                                                    label="Type"
                                                    value={item?.type}
                                                    fullWidth
                                                    disabled
                                                    sx={{
                                                        "& .MuiInputBase-root":
                                                            {
                                                                height: {
                                                                    xs: "48px",
                                                                    md: "56px",
                                                                },
                                                                fontSize: {
                                                                    xs: "16px",
                                                                    md: "18px",
                                                                },
                                                                backgroundColor:
                                                                    "#F5F5F5",
                                                            },
                                                    }}
                                                />
                                            </Box>
                                        ),
                                    )}
                                </Stack>
                            </Box>
                        )}
                </Stack>
            </FormProvider>
        </StepContainer>
    );
}
